/**
 * ShortlistParentReport — a print-optimised, single-page summary of
 * a student's saved shortlist designed to be opened by a parent or
 * career counsellor without any UI chrome. Users click "Print → Save
 * as PDF" to hand it over as a physical document; the same URL is
 * shareable directly.
 *
 * Design principles:
 *   • **Data first**: every row on the report is drawn from the same
 *     verified fields the student already sees on the site (no new
 *     numbers, no hallucination).
 *   • **Parent-friendly language**: no gap scores or industry
 *     jargon. Buckets are re-labelled ("A good chance" / "Worth
 *     applying" / "Reach").
 *   • **Print-first CSS**: `@media print` hides the navbar, footer,
 *     "Print / Save as PDF" button; sets an A4 page size; drops
 *     background colours to save ink; page-breaks between courses.
 *   • **Zero backend**: reuses `/shortlist` + `/admission/chance/
 *     bulk-degrees` — same endpoints the main app already calls, so
 *     numbers are guaranteed to match the site.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, LogIn, ArrowLeft, GraduationCap, Share2, Mail } from "lucide-react";
import { toast } from "sonner";
import { readApplicantProfile } from "@/lib/applicantProfile";
import { pickAcademicSignals } from "@/lib/academic-signals";
import { reportDegreeCost, rollupCosts, rollupCostLabel } from "@/lib/courseTuition";
import { publishedRateLabel } from "@/components/admission/AdmitProbabilityChip";
import { ApplicationFeesSection } from "@/components/app/ApplicationFeesSection";

const COUNTRY_FLAG = {
  UK: "🇬🇧", US: "🇺🇸", Germany: "🇩🇪", France: "🇫🇷",
  Australia: "🇦🇺", Canada: "🇨🇦", Netherlands: "🇳🇱", Ireland: "🇮🇪",
};

// Parent-friendly re-labelling of the internal buckets. The raw
// bucket labels ("Target", "Ambitious") are counsellor-speak; the
// report speaks to families.
const PARENT_BUCKET_LABEL = {
  likely: "A good chance",
  target: "Worth applying",
  ambitious: "Reach",
  highly_ambitious: "Long shot",
};

const PARENT_BUCKET_TONE = {
  likely: "text-emerald-800 border-emerald-400 bg-emerald-50",
  target: "text-amber-900 border-amber-400 bg-amber-50",
  ambitious: "text-orange-900 border-orange-400 bg-orange-50",
  highly_ambitious: "text-red-900 border-red-400 bg-red-50",
};

export function fmtMoney(v, ccy = "USD") {
  if (v == null) return null;
  const val = Number(v);
  if (!Number.isFinite(val)) return null;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: ccy,
    maximumFractionDigits: 0,
  }).format(val);
}

/**
 * Fetches a per-row calibrated admission probability for every
 * degree in the shortlist. This isn't stored on the shortlist row
 * itself — it depends on the applicant's grades — so we compute it
 * from the same profile the applicant already saved.
 */
export function useShortlistChances(items, applicantProfile) {
  const [chances, setChances] = useState({});
  useEffect(() => {
    if (!items.length || !applicantProfile) return;
    (async () => {
      try {
        // Bulk-degrees expects a singular `intended_degree`; applicant
        // profile stores `intended_degrees` as an array. Use the first
        // one to bias the classifier's subject match — the shortlist
        // may include multiple subjects but classifying against a
        // single "primary intent" is fine here because the shortlist
        // is the source of truth for which degrees appear in the loop.
        const primary = Array.isArray(applicantProfile.intended_degrees)
          ? applicantProfile.intended_degrees[0]
          : applicantProfile.intended_degree;
        const payload = {
          ...applicantProfile,
          intended_degree: primary || null,
          // Scope the server read to the shortlisted courses only.
          degree_ids: items.map((it) => it.degree_id).filter(Boolean),
        };
        const { data } = await api.post("/admission/chance/bulk-degrees", payload);
        // Index by degree_id so the render is O(1).
        const byDegree = {};
        for (const r of data.results || []) {
          if (r.degree_id) byDegree[r.degree_id] = r.classification;
        }
        setChances(byDegree);
      } catch {
        // Silent — the report degrades gracefully; probability just
        // won't render on rows we couldn't classify.
      }
    })();
  }, [items, applicantProfile]);
  return chances;
}

/**
 * One page-break-avoid card per degree. Prints one course per A4 page
 * when the browser respects `page-break-inside: avoid`.
 */
export function DegreeReportCard({ item, classification, index, onToggleCompare, isSelected }) {
  const d = item.degree;
  const u = item.university;
  if (!d) return null;

  const probability = classification?.admit_probability;
  const bucket = classification?.bucket;
  const parentLabel = bucket ? PARENT_BUCKET_LABEL[bucket] : null;
  const tone = bucket ? PARENT_BUCKET_TONE[bucket] : "text-stone-700 border-stone-300 bg-stone-50";

  // Uni logo — Wikipedia SVG for ~90% of the catalogue, editorial
  // logo_url for the rest, image_url as a last-ditch stock photo
  // fallback so families still see something rather than an initial-
  // only avatar. When even that's missing, we drop back to a
  // typographic monogram (first letter of the uni name in a purple
  // circle) so the card layout never collapses to a lopsided title.
  const logo = u?.logo_url || u?.image_url || null;
  const monogram = (u?.name || "?").trim().charAt(0).toUpperCase();

  // Cost resolution — tries a widening ladder so the tile falls back
  // gracefully as fewer fields are populated:
  //   1. degree.cost.tuition_intl_annual_usd — most specific
  //   2. degree.cost.tuition_domestic_annual_usd
  //   3. uni.tuition_intl_annual_usd (populated for 97% of US Scorecard)
  //   4. uni.tuition_domestic_annual_usd
  //   5. fees_annual_usd / cost.tuition_annual_usd (older seed shape)
  // Duration heuristic: `duration_years` is set for HESA + DAAD rows
  // but MANY US Scorecard rows leave it null. Standard US undergrad
  // is 4 years, UK undergrad is 3 — key off country when the record
  // itself hasn't specified.
  const years = d.duration_years
    || (u?.country === "US" ? 4 : 3);
  const accommodation = u?.accommodation_annual_usd || 0;
  const food = u?.food_annual_usd || 0;
  const costView = reportDegreeCost(d, u, years, accommodation + food, fmtMoney);
  const tuitionView = costView.tuition;
  const totalCost = costView.total;

  const academicSignals = pickAcademicSignals(u, d);
  const advertisedGrade = academicSignals.advertised?.value;
  const intakeGrade = academicSignals.intake?.value;
  // Prefer advertised as the primary; fall back to uni-wide typical
  // when neither advertised nor intake exist. Never silently swap
  // intake in as "the offer".
  const typicalGrade = advertisedGrade
    || academicSignals.uniWide?.value
    || (academicSignals.ucasTariff?.standard != null ? `${academicSignals.ucasTariff.standard} pts` : "See course page");
  const typicalGradeKind = advertisedGrade
    ? "advertised"
    : (academicSignals.uniWide?.value ? "uni_wide" : (academicSignals.ucasTariff?.standard != null ? "tariff" : "unknown"));
  const publishedRate = probability?.published_rate || null;

  const employers = d.outcomes?.top_employers
    || d.outcomes?.destinations?.top_employers
    || [];

  return (
    <div
      className={`parent-report-card break-inside-avoid border rounded-lg p-6 mb-8 bg-white transition-colors ${
        isSelected ? "border-[#4C1D95] ring-1 ring-[#4C1D95]" : "border-stone-300"
      }`}
      data-testid={`parent-report-card-${d.id}`}
    >
      <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          {/* Uni logo — wikipedia SVG or editorial fallback, with a
              typographic monogram if neither is on the record. */}
          {logo ? (
            <img
              src={logo}
              alt={`${u?.name || "University"} logo`}
              data-testid={`parent-report-logo-${d.id}`}
              className="w-14 h-14 rounded-md object-contain bg-stone-50 border border-stone-200 shrink-0 p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div
              className="w-14 h-14 rounded-md bg-[#4C1D95] text-white flex items-center justify-center font-editorial text-2xl shrink-0"
              data-testid={`parent-report-logo-monogram-${d.id}`}
            >
              {monogram}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              #{index + 1} · {u?.country ? `${COUNTRY_FLAG[u.country] || "🌍"} ${u.country}` : ""} · {d.qualification || "BSc"}
            </div>
            <div className="font-editorial text-2xl leading-tight text-stone-900">
              {d.official_title}
            </div>
            <div className="text-sm text-stone-700 mt-0.5">
              {u?.name}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {parentLabel && (
            <div
              data-testid={`parent-report-bucket-${d.id}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${tone}`}
            >
              {parentLabel}
            </div>
          )}
          {onToggleCompare && (
            <label
              className="flex items-center gap-1.5 text-[11px] text-stone-600 cursor-pointer print:hidden"
              title="Tick to compare with other courses on this report"
            >
              <input
                type="checkbox"
                checked={!!isSelected}
                onChange={() => onToggleCompare(d.id)}
                data-testid={`parent-report-compare-toggle-${d.id}`}
                className="rounded border-stone-400 text-[#4C1D95] focus:ring-[#4C1D95]"
              />
              Compare
            </label>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
            {publishedRate ? publishedRateLabel(publishedRate) : "Published admission rate"}
          </div>
          <div className="font-editorial text-xl mt-0.5 text-stone-900" data-testid="parent-card-published-rate">
            {publishedRate ? `${publishedRate.value_pct}%` : "Not available"}
          </div>
          {publishedRate && (
            <div className="text-[10px] text-stone-500 mt-0.5 leading-tight">
              University-wide, all courses · {publishedRate.cycle}
              <br />
              <span className="italic">{publishedRate.source} · not a personal chance</span>
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
            {typicalGradeKind === "advertised"
              ? "Advertised offer"
              : typicalGradeKind === "uni_wide"
                ? "Typical grade · uni-wide"
                : "Typical grade"}
          </div>
          <div
            className="font-editorial text-xl mt-0.5 text-stone-900"
            data-testid={`parent-report-typical-grade-${d.id}`}
          >
            {typicalGrade}
          </div>
          {intakeGrade && intakeGrade !== advertisedGrade && (
            <div
              className="text-[10px] text-stone-500 mt-0.5"
              data-testid={`parent-report-intake-grade-${d.id}`}
            >
              Intake · {intakeGrade} (HESA · what enrolled students scored)
            </div>
          )}
          {!intakeGrade && (
            <div className="text-[10px] text-stone-500 mt-0.5">
              {years}-year {d.qualification || "degree"}
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
            Total cost
          </div>
          <div className="font-editorial text-xl mt-0.5 text-stone-900">
            {totalCost ? `${tuitionView.indicative ? "≈ " : ""}${fmtMoney(totalCost)}` : (costView.label || "See course page")}
          </div>
          <div className="text-[10px] text-stone-500 mt-0.5" data-testid="report-tile-cost-basis">
            {totalCost ? `Tuition + living, all ${years} years` : "No all-years total"}{tuitionView.indicative ? " · indicative estimate" : ""}{tuitionView.qualifier ? ` · ${tuitionView.qualifier}` : ""}
          </div>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
            After graduation
          </div>
          <div className="font-editorial text-xl mt-0.5 text-stone-900">
            {d.outcomes?.median_starting_salary_usd
              ? fmtMoney(d.outcomes.median_starting_salary_usd)
              : d.hesa_stats?.salary_median_gbp
                ? `£${d.hesa_stats.salary_median_gbp.toLocaleString()}`
                : "—"}
          </div>
          <div className="text-[10px] text-stone-500 mt-0.5">
            Median 15 months post-grad
          </div>
        </div>
      </div>

      {/* Deadline strip — parents care about calendar pressure more
          than students do. Draws on `application_deadline` first,
          then the richer `application.closes` block, then a per-
          country default reminder if neither is set. */}
      {(() => {
        const app = d.application || {};
        const closes = d.application_deadline || app.closes;
        const decisionBy = app.decision_by;
        const platform = app.platform;
        const deadline = closes ? new Date(closes) : null;
        const isValidDate = deadline && !isNaN(deadline);
        // Days-until formatting stays on the client — the report is
        // "as of today from the reader's perspective".
        const now = new Date();
        const daysAway = isValidDate
          ? Math.ceil((deadline - now) / (1000 * 60 * 60 * 24))
          : null;
        const isPast = daysAway != null && daysAway < 0;
        const isNear = daysAway != null && daysAway >= 0 && daysAway <= 45;
        // Country-level fallback message so the strip still shows
        // something useful when the record has no deadline yet.
        if (!isValidDate && !platform) return null;
        return (
          <div
            data-testid={`parent-report-deadline-${d.id}`}
            className={`mb-4 flex flex-wrap items-center gap-2 text-[12px] rounded-md border px-3 py-2 ${
              isPast
                ? "border-red-300 bg-red-50 text-red-900"
                : isNear
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-stone-200 bg-stone-50 text-stone-700"
            }`}
          >
            <span className="font-mono text-[10px] uppercase tracking-wider opacity-80">
              Application
            </span>
            {platform && <span>· {platform}</span>}
            {isValidDate && (
              <span>
                · Deadline{" "}
                <strong>
                  {deadline.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </strong>
                {daysAway != null && !isPast && daysAway <= 90 && (
                  <span className="ml-1 opacity-80">({daysAway} days)</span>
                )}
                {isPast && <span className="ml-1 opacity-80">(passed)</span>}
              </span>
            )}
            {decisionBy && (
              <span>
                · Decision by <strong>{new Date(decisionBy).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</strong>
              </span>
            )}
          </div>
        );
      })()}
      {employers.length > 0 && (
        <div className="mb-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-1">
            Where graduates work
          </div>
          <div className="text-sm text-stone-800">
            {employers.slice(0, 6).map(e => typeof e === "string" ? e : e.name).join(" · ")}
          </div>
        </div>
      )}

      {d.summary && (
        <div className="text-[13px] text-stone-700 leading-relaxed border-t border-stone-100 pt-3">
          {d.summary}
        </div>
      )}

      <div className="mt-3 text-[10px] text-stone-500 print:hidden">
        <Link to={`/degree/${d.id}`} className="hover:text-[#4C1D95]">
          Full course page →
        </Link>
      </div>
    </div>
  );
}

export default function ShortlistParentReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [appFeesOn, setAppFeesOn] = useState(false);
  const [applicantProfile, setApplicantProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareSelected, setCompareSelected] = useState(() => new Set());
  const [showManage, setShowManage] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const chances = useShortlistChances(items, applicantProfile);

  const toggleCompare = (degreeId) => {
    setCompareSelected((prev) => {
      const next = new Set(prev);
      if (next.has(degreeId)) next.delete(degreeId);
      else if (next.size >= 3) {
        toast.info("You can compare up to 3 courses at a time");
        return prev;
      } else {
        next.add(degreeId);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const shortlistRes = await api.get("/shortlist");
        setItems(shortlistRes.data.items || []);
        setAppFeesOn(shortlistRes.data.application_fees_release === true);
        // Applicant profile is stored in sessionStorage (see
        // `lib/applicantProfile.js`) — Quick Check / Advanced
        // Wizard both write it on submit. Without it, the report
        // still renders (the shortlist is enough) but the per-row
        // calibrated probability chip can't be computed, so we
        // surface the missing-profile banner below.
        setApplicantProfile(readApplicantProfile());
      } catch {
        toast.error("Could not load your shortlist");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const rollup = useMemo(() => {
    if (!items.length) return null;
    const costRoll = rollupCosts(items);
    const employerSet = new Set();
    for (const it of items) {
      const emps = it.degree?.outcomes?.top_employers
        || it.degree?.outcomes?.destinations?.top_employers
        || [];
      for (const e of emps.slice(0, 3)) {
        employerSet.add(typeof e === "string" ? e : e.name);
      }
    }
    return {
      count: items.length,
      countries: new Set(items.map(it => it.university?.country).filter(Boolean)).size,
      ...costRoll,
      topEmployers: [...employerSet].slice(0, 8),
    };
  }, [items]);

  if (loading) return null;
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="label-uppercase">Sign in required</div>
        <p className="mt-2 text-stone-600">Save some courses first, then generate a parent-ready report.</p>
        <Link to="/">
          <Button className="mt-6" data-testid="parent-report-signin-btn">
            <LogIn className="h-4 w-4 mr-2" /> Sign in
          </Button>
        </Link>
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="parent-report-empty">
        <div className="label-uppercase">No shortlist yet</div>
        <p className="mt-2 text-stone-600">
          Save at least one course from Explore or a Quick Check, then come
          back to print your parent report.
        </p>
        <Link to="/explore">
          <Button className="mt-6">Browse courses</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="parent-report-root max-w-4xl mx-auto px-6 py-10">
      {/* Screen-only toolbar. Print hides via `.print:hidden`. */}
      <div className="print:hidden flex flex-wrap items-center justify-between gap-2 mb-6">
        <Button
          variant="outline"
          onClick={() => navigate("/shortlist")}
          data-testid="parent-report-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to shortlist
        </Button>
        <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
          <Button
            variant="ghost"
            onClick={() => setShowManage(true)}
            data-testid="parent-report-manage-links-btn"
            title="See or revoke shared links you've created"
          >
            Manage shared links
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowEmailModal(true)}
            data-testid="parent-report-email-btn"
            title="Email this report to a parent or counsellor"
          >
            <Mail className="h-4 w-4 mr-2" /> Email to parent
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                // Take a server-side snapshot so the recipient can
                // load the exact same shortlist + profile without
                // logging in. Falls back to the private URL if the
                // snapshot POST fails (e.g. empty shortlist).
                let url = window.location.origin + "/shortlist/parent-report";
                try {
                  const { data } = await api.post(
                    "/shortlist/parent-report/snapshot",
                    { applicant_profile: applicantProfile || {} },
                  );
                  if (data?.slug) {
                    url = `${window.location.origin}/shared/parent-report/${data.slug}`;
                  }
                } catch {
                  // No snapshot — fall through to the private URL.
                }
                if (navigator.share) {
                  await navigator.share({
                    title: "My university shortlist",
                    text: "Here's the shortlist of universities I'm considering.",
                    url,
                  });
                } else {
                  await navigator.clipboard.writeText(url);
                  toast.success(
                    "Shareable link copied. Anyone with this link can view the report for 30 days.",
                  );
                }
              } catch {
                toast.error("Could not create shareable link");
              }
            }}
            data-testid="parent-report-share-btn"
            title="Share this report with a parent or counsellor (no login needed)"
          >
            <Share2 className="h-4 w-4 mr-2" /> Share
          </Button>
          <Button
            onClick={() => window.print()}
            className="bg-[#4C1D95] hover:bg-[#3d1479] text-white"
            data-testid="parent-report-print-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Manage shared links modal — shown when the student clicks
          "Manage shared links". Lists active snapshots with age +
          revoke buttons. Uses the same GET/DELETE endpoints on the
          backend that were added in v8.85. */}
      {showManage && (
        <ManageSharedLinksModal onClose={() => setShowManage(false)} />
      )}

      {/* Email-to-parent modal — student enters the parent's email
          (must be verified on our Resend account while we're in
          testing mode), an optional personal note, and we POST
          /shortlist/parent-report/snapshot/{slug}/email after
          creating a fresh snapshot. */}
      {showEmailModal && (
        <EmailReportModal
          applicantProfile={applicantProfile}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* Missing-applicant-profile banner. When the family hasn't yet
          run a Quick Check, per-course probability chips can't be
          computed — but the shortlist itself is still worth showing.
          A dismissible inline banner is nicer than the previous toast
          because parents opening the print URL directly will see
          this once and can act on it. */}
      {!applicantProfile && (
        <div
          className="print:hidden mb-6 rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="parent-report-missing-profile-banner"
        >
          <div className="shrink-0 mt-0.5 rounded-full bg-amber-200 p-1.5">
            <GraduationCap className="h-4 w-4 text-amber-900" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900 text-sm">
              Complete a Quick Check for personalised probabilities
            </div>
            <div className="text-xs text-amber-800 mt-0.5 leading-snug">
              Without your grades, we can't show how likely each course is
              to give you an offer. Takes 30 seconds and populates every
              card on this report.
            </div>
          </div>
          <Link to="/admission" data-testid="parent-report-quick-check-cta">
            <Button
              size="sm"
              className="bg-amber-900 hover:bg-amber-800 text-white"
            >
              Quick Check →
            </Button>
          </Link>
        </div>
      )}

      {/* Cover / rollup */}
      <div
        className="border-b-2 border-stone-900 pb-6 mb-8"
        data-testid="parent-report-cover"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              Which University Advisor · Family-ready shortlist report
            </div>
            <h1 className="font-editorial text-4xl text-stone-900 leading-tight">
              {user.name || user.email}'s shortlist
            </h1>
            {applicantProfile && (
              <div className="mt-2 text-sm text-stone-700">
                {applicantProfile.curriculum?.toUpperCase() || "Applicant"}
                {applicantProfile.a_level && ` · A-Level ${applicantProfile.a_level}`}
                {applicantProfile.ib && ` · IB ${applicantProfile.ib}`}
                {applicantProfile.sat_total && ` · SAT ${applicantProfile.sat_total}`}
                {applicantProfile.intended_degree && ` · aiming for ${applicantProfile.intended_degree}`}
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-2 text-[#4C1D95]">
            <GraduationCap className="h-8 w-8" />
          </div>
        </div>

        {rollup && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <RollupTile label="Courses saved" value={rollup.count} />
            <RollupTile
              label="Countries"
              value={rollup.countries}
              testId="rollup-countries"
            />
            <RollupTile
              label="Total cost range"
              value={rollupCostLabel(rollup, fmtMoney)}
              testId="rollup-cost-range"
            />
          </div>
        )}

        {rollup?.topEmployers?.length > 0 && (
          <div className="mt-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              Employers that hire from this shortlist
            </div>
            <div className="text-sm text-stone-800">
              {rollup.topEmployers.join(" · ")}
            </div>
          </div>
        )}
      </div>

      {/* Compare tray — appears on-screen only (never printed) as
          soon as 2+ courses are ticked. Prints a side-by-side
          table with the deltas that matter most to parents. */}
      {compareSelected.size >= 2 && (
        <CompareTable
          selectedIds={[...compareSelected]}
          items={items}
          chances={chances}
        />
      )}

      {appFeesOn && <ApplicationFeesSection items={items} />}

      {/* Per-degree cards */}
      <div data-testid="parent-report-degrees">
        {items.map((item, i) => (
          <DegreeReportCard
            key={item.degree_id || item.university_id}
            item={item}
            classification={chances[item.degree_id]}
            index={i}
            onToggleCompare={toggleCompare}
            isSelected={compareSelected.has(item.degree_id)}
          />
        ))}
      </div>

      {/* Report footer — appears on the last printed page */}
      <div className="mt-10 pt-6 border-t border-stone-200 text-[11px] text-stone-500 leading-relaxed">
        <p>
          Numbers on this report come from the same verified sources
          the student sees on <strong>whichuniversityadvisor.com</strong>:
          UCAS End-of-Cycle acceptance rates, HESA Discover Uni salary
          data, US College Scorecard, and each university's own
          entry-requirements page. "How realistic" is a rule-based
          grade match, not a prediction. The published acceptance rate
          is a university-wide figure for past applicants, not a
          personal chance.
        </p>
        <p className="mt-2">
          Generated for {user.email} on {new Date().toLocaleDateString()}.
        </p>
      </div>

      {/* Print-only CSS. Hides site chrome, forces per-course page breaks,
          drops backgrounds so ink usage is minimal. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { background: white !important; }
          nav, header, footer, .print\\:hidden { display: none !important; }
          .parent-report-root { max-width: none; padding: 0; }
          .parent-report-card {
            page-break-inside: avoid;
            break-inside: avoid;
            box-shadow: none !important;
            background: white !important;
          }
          .break-inside-avoid { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

export function RollupTile({ label, value, testId }) {
  return (
    <div className="border border-stone-200 rounded-md p-3 bg-stone-50" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
        {label}
      </div>
      <div className="font-editorial text-lg mt-0.5 text-stone-900 leading-tight">
        {value}
      </div>
    </div>
  );
}



/**
 * CompareTable — appears above the per-degree cards when the reader
 * has ticked 2 or 3 courses. Side-by-side table of the four numbers
 * families ask about most in that order: chance, cost, salary,
 * deadline. Prints as part of the report.
 */
export function CompareTable({ selectedIds, items, chances }) {
  const cols = selectedIds
    .map((id) => items.find((it) => it.degree_id === id))
    .filter(Boolean);
  if (cols.length < 2) return null;

  const cell = (item) => {
    const d = item.degree || {};
    const u = item.university || {};
    const c = chances[item.degree_id] || {};
    const p = c.admit_probability;
    const years = d.duration_years || (u.country === "US" ? 4 : 3);
    const acc = u.accommodation_annual_usd || 0;
    const food = u.food_annual_usd || 0;
    const costView = reportDegreeCost(d, u, years, acc + food, fmtMoney);
    const tuitionView = costView.tuition;
    const totalCost = costView.total;
    const closes = d.application_deadline || d.application?.closes;
    const sig = pickAcademicSignals(u, d);
    const adv = sig.advertised?.value;
    const intk = sig.intake?.value;
    const uniW = sig.uniWide?.value;
    return {
      chance: p?.published_rate ? `${p.published_rate.value_pct}%` : "Not available",
      chanceBase: p?.published_rate ? `${publishedRateLabel(p.published_rate).replace("Published ", "")} · university-wide · ${p.published_rate.cycle}` : null,
      cost: totalCost ? `${tuitionView.indicative ? "≈ " : ""}${fmtMoney(totalCost)}${tuitionView.indicative ? " (indicative)" : ""}` : (costView.label || "See course page"),
      salary: d.outcomes?.median_starting_salary_usd
        ? fmtMoney(d.outcomes.median_starting_salary_usd)
        : d.hesa_stats?.salary_median_gbp
          ? `£${d.hesa_stats.salary_median_gbp.toLocaleString()}`
          : "—",
      deadline: closes
        ? new Date(closes).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
        : "—",
      bucket: c.bucket ? PARENT_BUCKET_LABEL[c.bucket] : "—",
      // Two distinct grade signals — never conflated. Advertised is
      // the promise; intake is what actually enrolled scored.
      advertised: adv || (uniW ? `${uniW} (uni-wide)` : "—"),
      intake: intk ? `${intk} (HESA)` : "—",
    };
  };

  const rows = [
    { key: "bucket",     label: "How realistic" },
    { key: "chance",     label: "Published admission rate", secondaryKey: "chanceBase" },
    { key: "advertised", label: "Advertised offer (A-Level)" },
    { key: "intake",     label: "Typical intake (A-Level)" },
    { key: "cost",       label: "Total cost (tuition + living)" },
    { key: "salary",     label: "Median starting salary" },
    { key: "deadline",   label: "Application deadline" },
  ];

  const data = cols.map((it) => cell(it));

  return (
    <div
      className="mb-8 rounded-lg border-2 border-[#4C1D95] bg-white overflow-hidden"
      data-testid="parent-report-compare-table"
    >
      <div className="px-4 py-3 bg-[#4C1D95]/5 border-b border-[#4C1D95]/20">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#4C1D95]">
          Side-by-side comparison
        </div>
        <div className="font-editorial text-lg text-stone-900 mt-0.5">
          {cols.length} courses · the deltas that matter
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="text-left px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500 w-[220px]">
                &nbsp;
              </th>
              {cols.map((it) => (
                <th
                  key={it.degree_id}
                  className="text-left px-4 py-2 font-semibold text-stone-900"
                  data-testid={`compare-col-${it.degree_id}`}
                >
                  <div className="text-[13px] leading-tight">
                    {it.degree?.official_title}
                  </div>
                  <div className="text-[11px] text-stone-600 font-normal mt-0.5">
                    {it.university?.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-stone-100 last:border-b-0">
                <td className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">
                  {r.label}
                </td>
                {data.map((d, i) => (
                  <td
                    key={i}
                    className="px-4 py-2 text-stone-900"
                    data-testid={`compare-cell-${r.key}-${cols[i].degree_id}`}
                  >
                    <div className="font-editorial text-base">{d[r.key] || "—"}</div>
                    {r.secondaryKey && d[r.secondaryKey] && (
                      <div className="text-[10px] text-stone-500">{d[r.secondaryKey]}</div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/**
 * ManageSharedLinksModal — small panel listing every parent-report
 * snapshot the current user still owns. Each row shows the slug,
 * when it was created + expires, and a preview of the courses on
 * it. Revoking hard-deletes the snapshot server-side; the shared
 * URL 404s immediately.
 */
function ManageSharedLinksModal({ onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/shortlist/parent-report/snapshots");
      setRows(data.snapshots || []);
    } catch (e) {
      setError("Could not load your shared links");
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (slug) => {
    if (!window.confirm("Revoke this shared link? The recipient will lose access immediately.")) return;
    try {
      await api.delete(`/shortlist/parent-report/snapshot/${slug}`);
      toast.success("Link revoked");
      setRows((r) => (r || []).filter((x) => x.slug !== slug));
    } catch {
      toast.error("Could not revoke link");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 print:hidden"
      onClick={onClose}
      data-testid="parent-report-manage-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl border border-stone-200 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
              Shared links
            </div>
            <div className="font-editorial text-xl mt-0.5">
              Manage your report shares
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="parent-report-manage-close">
            Close
          </Button>
        </div>
        <div className="p-6 space-y-3">
          {error && <div className="text-sm text-red-700">{error}</div>}
          {rows === null && !error && (
            <div className="text-sm text-stone-500">Loading…</div>
          )}
          {rows && rows.length === 0 && (
            <div
              className="text-sm text-stone-500 italic"
              data-testid="parent-report-manage-empty"
            >
              You haven't created any shareable links yet. Click "Share"
              on the toolbar to create one — recipients get a 30-day
              read-only view without needing to sign in.
            </div>
          )}
          {rows && rows.map((r) => {
            const url = `${window.location.origin}/shared/parent-report/${r.slug}`;
            const created = r.created_at ? new Date(r.created_at) : null;
            const expires = r.expires_at ? new Date(r.expires_at) : null;
            const daysLeft = expires
              ? Math.max(0, Math.ceil((expires - new Date()) / 86400000))
              : null;
            return (
              <div
                key={r.slug}
                data-testid={`parent-report-manage-row-${r.slug}`}
                className="rounded-md border border-stone-200 p-3 hover:border-stone-400 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-mono text-xs text-stone-500">
                        {r.slug.substring(0, 12)}…
                      </span>
                      {daysLeft != null && (
                        <span
                          className={`text-[10px] font-underground-sc tracking-wider ${
                            daysLeft <= 3 ? "text-red-700" : "text-stone-500"
                          }`}
                        >
                          {daysLeft} days left
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[13px] text-stone-800">
                      {r.preview_count} courses ·{" "}
                      {r.preview_names?.slice(0, 3).join(" · ") || "—"}
                    </div>
                    <div className="mt-1 text-[10px] text-stone-500">
                      Created {created ? created.toLocaleDateString() : "?"}{" "}
                      · expires {expires ? expires.toLocaleDateString() : "?"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`parent-report-manage-copy-${r.slug}`}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(url);
                          toast.success("Link copied");
                        } catch { toast.error("Copy failed"); }
                      }}
                    >
                      Copy link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revoke(r.slug)}
                      data-testid={`parent-report-manage-revoke-${r.slug}`}
                      className="text-red-700 hover:text-red-900 hover:bg-red-50"
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/**
 * EmailReportModal — collects parent's email + optional note, creates
 * a fresh 30-day snapshot server-side, then POSTs the send-email
 * endpoint. Two-step flow (snapshot → email) is intentional: it
 * guarantees the recipient gets a link tied to the shortlist at the
 * moment of sending, even if the student edits later.
 *
 * Testing-mode caveat: Resend's default `onboarding@resend.dev`
 * sender only delivers to email addresses YOU have verified on your
 * Resend dashboard. When the sending domain is DNS-verified (edit
 * `RESEND_SENDER_EMAIL` in backend `.env`) this restriction lifts
 * automatically — no frontend change needed.
 */
function EmailReportModal({ applicantProfile, onClose }) {
  const [parentEmail, setParentEmail] = useState("");
  const [parentName, setParentName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!parentEmail || !parentEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSending(true);
    try {
      // Step 1 — snapshot the current shortlist so the recipient sees
      // exactly what the sender is looking at right now.
      const snap = await api.post("/shortlist/parent-report/snapshot", {
        applicant_profile: applicantProfile || {},
      });
      // Step 2 — Resend fires the email with the public share URL.
      await api.post(
        `/shortlist/parent-report/snapshot/${snap.data.slug}/email`,
        {
          parent_email: parentEmail.trim(),
          parent_name: parentName.trim(),
          personal_note: note.trim(),
        },
      );
      setSent(true);
      toast.success(`Report emailed to ${parentEmail}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send email";
      // Resend's most common error while in testing mode is
      // "You can only send emails to verified email addresses" —
      // surface a friendly hint when we see it.
      if (typeof detail === "string" && /verified/i.test(detail)) {
        toast.error(
          "Resend is in testing mode — only email addresses verified on the sender's account will receive mail. Verify this address on resend.com/audiences or add your custom sending domain.",
          { duration: 10000 },
        );
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 print:hidden"
      onClick={onClose}
      data-testid="parent-report-email-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl border border-stone-200 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">
              Email report
            </div>
            <div className="font-editorial text-xl mt-0.5">Send to a parent</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="parent-report-email-close">
            Close
          </Button>
        </div>
        {sent ? (
          <div className="p-6 space-y-3" data-testid="parent-report-email-sent">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
              <div className="font-semibold text-emerald-900">Sent!</div>
              <div className="text-sm text-emerald-800 mt-1">
                {parentEmail} will receive the report within a minute.
                The read-only link inside the email expires in 30 days.
              </div>
            </div>
            <Button
              className="w-full bg-[#4C1D95] hover:bg-[#3d1479] text-white"
              onClick={onClose}
              data-testid="parent-report-email-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={send} className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-underground-sc tracking-wider text-stone-500 mb-1">
                Parent's email
              </label>
              <input
                type="email"
                required
                autoFocus
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="mum@example.com"
                data-testid="parent-report-email-to-input"
                className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-underground-sc tracking-wider text-stone-500 mb-1">
                Their name <span className="normal-case font-sans text-stone-400">(optional)</span>
              </label>
              <input
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="Mum"
                data-testid="parent-report-email-name-input"
                className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-underground-sc tracking-wider text-stone-500 mb-1">
                Personal note <span className="normal-case font-sans text-stone-400">(optional)</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                placeholder="Have a look and let me know which ones you'd like to talk through this weekend."
                rows={3}
                data-testid="parent-report-email-note-input"
                className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95] outline-none resize-none"
              />
              <div className="text-[10px] text-stone-400 mt-1 text-right">
                {note.length}/500
              </div>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 leading-snug">
              <strong>Heads up:</strong> we're in Resend testing mode. Emails only
              deliver to addresses verified on the WhichUniversityAdvisor
              Resend account. Ask your admin to verify this address if you get
              a "not verified" error.
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-[#4C1D95] hover:bg-[#3d1479] text-white"
              data-testid="parent-report-email-send-btn"
            >
              {sending ? "Sending…" : "Send email"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

